import { Job, JobSource, SystemWorkOrderStatus, WorkOrder, WorkOrderUtility } from '@jms/model';
import { DateTime } from 'luxon';
import { JobRight } from '@workbuddy/ui/modules/manage.job.shared/model';

/** Stages in these statuses never read as overdue or due today. */
const SETTLED_STATUSES = [SystemWorkOrderStatus.Completed, SystemWorkOrderStatus.Finalised, SystemWorkOrderStatus.Closed, SystemWorkOrderStatus.Canceled];

export class JobManageUtility {

    /**
     * Whole days between a stage's due date and today in the site's timezone, or null when the stage
     * has no due date or is settled. Negative = the due day has passed, zero = due today. Day-based
     * on purpose: the editor warns about the due *day*, unlike the time-of-day comparison a dispatch
     * board would use.
     */
    public static stageDueDayDiff(workOrder: WorkOrder, zone: string): number {

        if (!workOrder?.dueDate) return null;

        if (SETTLED_STATUSES.includes(workOrder.status?.systemStatus)) return null;

        const due = DateTime.fromJSDate(new Date(workOrder.dueDate), zone ? { zone } : undefined).startOf('day');
        const today = (zone ? DateTime.now().setZone(zone) : DateTime.now()).startOf('day');

        return due.diff(today, 'days').days;
    }

    public static woReadOnlyAfterFinalised(workOrder: WorkOrder, right: JobRight): boolean {

        let readOnly = true;

        const finalized = WorkOrderUtility.isSystemStatusFinalised(workOrder?.status);

        if (right?.modifyJobAfterFinalised && finalized) {
            readOnly = false;
        } else {
            readOnly = JobManageUtility.workOrderReadOnly(workOrder, JobSource.Active, right);
        }

        return readOnly;
    }

    public static technicianReadOnly(workOrder: WorkOrder, source: JobSource, right: JobRight): boolean {

        const closedSources = [
            JobSource.Archive,
            JobSource.Closed
        ];

        const workOrderClosed = WorkOrderUtility.isSystemStatusClosed(workOrder?.status);

        const isClosedSource = closedSources.includes(source);

        const notAllowToAddTechnician = [
            SystemWorkOrderStatus.Completed,
            SystemWorkOrderStatus.OnHold,
            SystemWorkOrderStatus.Finalised,
            SystemWorkOrderStatus.Closed,
        ] as string[];

        const readOnly = !right?.manage
            || workOrderClosed
            || isClosedSource
            || (notAllowToAddTechnician.includes(workOrder?.status.systemStatus) && !workOrder?.technician?._id);

        return readOnly;
    }

    public static workOrderReadOnly(workOrder: WorkOrder, source: JobSource, right: JobRight): boolean {

        const closedSources = [
            JobSource.Archive,
            JobSource.Closed
        ];

        const workOrderClosed = WorkOrderUtility.isSystemStatusClosed(workOrder?.status);
        const finalized = WorkOrderUtility.isSystemStatusFinalised(workOrder?.status);

        const isClosedSource = closedSources.includes(source);

        const readOnly = !right?.manage || workOrderClosed || isClosedSource || finalized;

        return readOnly;

    }

    public static jobReadOnly(job: Job, right: JobRight): boolean {

        const closedSystemStatus = [
            SystemWorkOrderStatus.Closed,
            SystemWorkOrderStatus.Canceled
        ] as string[];

        const jobFinalised = job?.status === SystemWorkOrderStatus.Finalised;
        const jobClosed = closedSystemStatus.includes(job?.status);

        const readOnly = !right?.manage || jobClosed || jobFinalised;

        return readOnly;
    }

    public static jobReadOnlyAfterFinalised(job: Job, right: JobRight): boolean {

        if (!job) return true;

        let readOnly = true;

        const finalized = job.status === SystemWorkOrderStatus.Finalised;

        if (right?.modifyJobAfterFinalised && finalized) {
            readOnly = false;
        } else {
            readOnly = JobManageUtility.jobReadOnly(job, right);
        }

        return readOnly;
    }

    public static allowAddWorkOrder(job: Job, right: JobRight): boolean {

        const closedSystemStatus = [
            SystemWorkOrderStatus.Closed,
            SystemWorkOrderStatus.Canceled
        ] as string[];

        const jobClosed = closedSystemStatus.includes(job?.status);

        const jobReadOnly = JobManageUtility.jobReadOnly(job, right);

        // Advanced and Project both append stages to a finalised job under modifyJobAfterFinalised —
        // Project stage creation is append-only by design. Basic has no stage-add surface at all.
        // Modify Job after Finalise widens Jobs Manage; it never stands in for it.
        const readOnly = (right?.manage && right?.modifyJobAfterFinalised && (job.mode !== 'Basic') && !jobClosed)
            ? true
            : !jobReadOnly;

        return readOnly;
    }

}
